import { Link } from 'react-router';
import type { FilterSearchProps } from '~/components/common/FilterSearch';
import { FilterSearch } from '~/components/common/FilterSearch';
import { EnabledIndicator } from '~/components/common/icons/EnabledIndicator';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import { LifecycleStatusIcon } from '~/components/common/icons/LifecycleStatusIcon';
import type { SortIndicatorProps } from '~/components/common/icons/SortIndicator';
import { NoItemsRow } from '~/components/common/NoItemsRow';
import { SortTrigger } from '~/components/common/SortTrigger';
import { Tooltip } from '~/components/common/Tooltip';
import * as routePaths from '~/paths';
import type { SortableStudy } from '~/routes/upload/model/__uploadModelsPage/studies';
import { readableGqlEnum } from '~/utils/text';

type Props = {
  studies: SortableStudy[];
  sortIndicatorProps: SortIndicatorProps;
  filterSearchProps: FilterSearchProps;
};

export function StudyList({
  studies,
  sortIndicatorProps: siProps,
  filterSearchProps,
}: Props) {
  return (
    <div className="space-y-4">
      <FilterSearch {...filterSearchProps} />

      <table className="table table-compact w-full table-pin-rows">
        <thead>
          <tr>
            <th className="text-center lg:col-span-1/12">
              <SortTrigger
                colName="approvalState"
                sortIndicatorProps={siProps}
                filterable
              >
                <Tooltip message="Approved">
                  <span>Appr.</span>
                </Tooltip>
              </SortTrigger>
            </th>
            <th className="text-center">
              <SortTrigger colName="id" sortIndicatorProps={siProps}>
                ID
              </SortTrigger>
            </th>
            <th>
              <SortTrigger
                colName="type"
                sortIndicatorProps={siProps}
                filterable
              >
                Type
              </SortTrigger>
            </th>
            <th>
              <SortTrigger colName="name" sortIndicatorProps={siProps}>
                Name
              </SortTrigger>
            </th>
            <th>
              <SortTrigger
                colName="geologyTypes"
                sortIndicatorProps={siProps}
                filterable
                renderFilterOption={readableGqlEnum}
              >
                Geo.
              </SortTrigger>
            </th>
            <th>
              <SortTrigger
                colName="dataHistory.collectedBy"
                sortIndicatorProps={siProps}
                filterable
              >
                Author
              </SortTrigger>
            </th>
            <th>
              <SortTrigger
                colName="qualityParameters.publicationType"
                sortIndicatorProps={siProps}
              >
                Pub. Type
              </SortTrigger>
            </th>
            <th>
              <Tooltip message="# Measurements / # AEs">
                <SortTrigger
                  colName="measurementCount"
                  sortIndicatorProps={siProps}
                >
                  Meas.
                </SortTrigger>
              </Tooltip>
            </th>
            <th>
              <SortTrigger
                colName="hasOutcrops"
                sortIndicatorProps={siProps}
                filterable
                renderFilterOption={value =>
                  value === 'true'
                    ? 'Has linked outcrops'
                    : 'No linked outcrops'
                }
              >
                Outcrops
              </SortTrigger>
            </th>
            <th>
              <SortTrigger
                colName="project.name"
                sortIndicatorProps={siProps}
                filterable
              >
                Project
              </SortTrigger>
            </th>
            <th className="text-center">
              <Tooltip message="Whether the study has been merged or needs to be QC'd.">
                <SortTrigger
                  colName="qcCompleted"
                  sortIndicatorProps={siProps}
                  filterable
                >
                  QC
                </SortTrigger>
              </Tooltip>
            </th>
          </tr>
        </thead>

        <tbody>
          <NoItemsRow show={!studies.length} />
          {studies.map(s => (
            <StudyListRow key={s.id} study={s} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StudyListRow({ study }: { study: SortableStudy }) {
  return (
    <tr>
      <td className="text-center">
        <LifecycleStatusIcon status={study.lifecycleStatus} />
      </td>
      <td className="text-center">{study.id}</td>
      <td>{study.type}</td>
      <td>
        <Link
          to={routePaths.uploadStudyUpdateRoute(parseInt(study.id))}
          className="link"
        >
          {study.name}
        </Link>
      </td>
      <td className="space-x-1">
        {study.geologyTypes.map(geologyType => (
          <GeologyTypeIconV4
            key={geologyType}
            geologyType={geologyType}
            className="w-5 h-5"
            hideTooltip
          />
        ))}
      </td>
      <td>{study.collectedBy}</td>
      <td>{study.publicationType?.join(', ')}</td>
      <td>
        {study.measurementCount} / {study.architecturalElementCount}
      </td>
      <td>
        {!study.hasOutcrops && (
          <div className="text-center">
            <EnabledIndicator value={false} />
          </div>
        )}
        {study.outcrops.map(oc => (
          <div key={oc.id}>
            <Link
              to={routePaths.uploadOutcropUpdateRoute(parseInt(oc.id))}
              className="link"
            >
              {oc.name}
            </Link>
          </div>
        ))}
      </td>
      <td>
        <Link
          to={routePaths.uploadProjectUpdateRoute(parseInt(study.project.id))}
          className="link"
        >
          {study.project.name}
        </Link>
      </td>
      <td className=" text-center">
        <EnabledIndicator
          value={study.qcCompleted}
          className={study.redundant ? 'text-muted' : undefined}
        />
      </td>
    </tr>
  );
}
